/**
 * Proposals (`prp_…`): proposed rule lists, event type settings, and partial
 * changes stored for a person to review. The event type and partial editors
 * load one with `?proposal=prp_…`; applying it is the editor's normal save
 * (sent with `proposal_id`), and dismissing it closes it without changes.
 */
import { queryOptions, useMutation } from '@tanstack/react-query'
import { api, unwrap } from '../client'
import { queryKeys } from '../keys'
import type { ProposalDetail, ProposalKind, ProposalSummary } from '../types'

export interface ProposalListFilter {
  /** Application ID; omit for every application. */
  app?: string
  /** With `app`, also list proposals about shared partials. */
  includeShared?: boolean
  targetId?: string
  kind?: ProposalKind
}

export const proposalKeys = {
  all: ['proposals'] as const,
  list: (f: ProposalListFilter) => ['proposals', 'list', f] as const,
  counts: (app: string | undefined) => ['proposals', 'counts', app ?? 'all'] as const,
  detail: (id: string) => ['proposals', 'detail', id] as const,
}

export const proposalQuery = (id: string) =>
  queryOptions({
    queryKey: proposalKeys.detail(id),
    queryFn: () =>
      unwrap(
        api.GET('/admin/v1/proposals/{id}', { params: { path: { id } } }),
      ) as Promise<ProposalDetail>,
    // `stale` and `check_errors` change when the live target does.
    staleTime: 0,
  })

/** Open, unexpired proposals, newest first (the first 200). */
export const openProposalsQuery = (f: ProposalListFilter) =>
  queryOptions({
    queryKey: proposalKeys.list(f),
    queryFn: async (): Promise<{ items: ProposalSummary[]; more: boolean }> => {
      const page = await unwrap(
        api.GET('/admin/v1/proposals', {
          params: {
            query: {
              app: f.app,
              include_shared: f.app && f.includeShared ? true : undefined,
              target_id: f.targetId,
              kind: f.kind,
              status: 'open',
              limit: 200,
            },
          },
        }),
      )
      return { items: page.items, more: !!page.next_cursor }
    },
    staleTime: 15_000,
  })

/** How many open proposals are in the app scope (with shared partials), for the nav badge. */
export const proposalCountsQuery = (app: string | undefined) =>
  queryOptions({
    queryKey: proposalKeys.counts(app),
    queryFn: () =>
      unwrap(
        api.GET('/admin/v1/proposals/counts', {
          params: { query: { app, include_shared: app ? true : undefined } },
        }),
      ),
    staleTime: 30_000,
    refetchInterval: 120_000,
  })

/** Dismiss an open proposal. Refreshes proposal lists, counts, and the targets' badges. */
export function useDismissProposal() {
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      unwrap(
        api.POST('/admin/v1/proposals/{id}/dismiss', {
          params: { path: { id } },
          body: { note: note?.trim() || null },
        }),
      ) as Promise<ProposalDetail>,
    meta: {
      invalidates: [proposalKeys.all, queryKeys.eventTypes.all, ['partials']],
      successMessage: 'Proposal dismissed',
    },
  })
}

/** Query keys to refresh after a save applied a proposal. */
export const APPLIED_PROPOSAL_KEYS = [proposalKeys.all] as const

/** `?proposal=` values the editors accept. */
export const PROPOSAL_ID = /^prp_[0-9A-HJKMNP-TV-Z]{26}$/

/** A client label as people read it (`claude` → `Claude`). */
export function viaLabel(via: string): string {
  if (via === 'claude') return 'Claude'
  return via.charAt(0).toUpperCase() + via.slice(1)
}
